import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import {
  PROPOSAL_BUNDLE_CATEGORY,
  type ProposalBundle,
  type ProposalBundleCategory
} from '../../types/proposalBundles';
import TransportCard from './TransportCard';
import HotelCard from './HotelCard';

interface Props {
  bundles: ProposalBundle[];
  includeTax: boolean;
  introMessage?: string;
  onSelect?: (bundleId: number, itemId: number) => void;
  onClose?: (bundleId: number) => void;
  disabled?: boolean;
  tripId: number;
}

const isTransportCategory = (category: ProposalBundleCategory): boolean => {
  const movementCategories: readonly ProposalBundleCategory[] = [
    PROPOSAL_BUNDLE_CATEGORY.OUTWARD,
    PROPOSAL_BUNDLE_CATEGORY.HOMEWARD,
    PROPOSAL_BUNDLE_CATEGORY.TRANSPORT
  ];
  return movementCategories.includes(category);
};

const isHotelCategory = (category: ProposalBundleCategory): boolean => {
  return category === PROPOSAL_BUNDLE_CATEGORY.HOTEL;
};

const ProposalCards = ({
  bundles,
  includeTax,
  introMessage,
  onSelect,
  onClose,
  disabled = false,
  tripId
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  if (bundles.length === 0) {
    return null;
  }

  return (
    <div style={styles.container}>
      {introMessage && <div style={styles.introMessage}>{introMessage}</div>}
      {bundles.map((bundle, index) => {
        const productNumber = index + 1;

        if (isTransportCategory(bundle.category)) {
          return (
            <TransportCard
              key={bundle.id}
              bundle={bundle}
              productNumber={productNumber}
              includeTax={includeTax}
              onSelect={onSelect}
              onClose={onClose}
              disabled={disabled}
            />
          );
        }

        if (isHotelCategory(bundle.category)) {
          return (
            <HotelCard
              key={bundle.id}
              bundle={bundle}
              productNumber={productNumber}
              includeTax={includeTax}
              onSelect={onSelect}
              onClose={onClose}
              disabled={disabled}
              tripId={tripId}
            />
          );
        }

        return null;
      })}
      <div style={styles.instructionBox}>
        <div style={styles.instructionHeader}>
          <span style={styles.instructionIcon}>📝</span>
          <span style={styles.instructionTitle}>選択方法</span>
        </div>
        <ul style={styles.instructionList}>
          <li>各商品の「選択」ボタンをクリックしてください。</li>
          <li>選択状況は右側のパネルでリアルタイムに確認できます。</li>
          <li>すべての商品を選択したら、次のステップに進みます。</li>
        </ul>
        <p style={styles.instructionNote}>※不要な商品がある場合、カード右上の「×」を押してください。</p>
      </div>
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    width: '100%',
    backgroundColor: '#fcfcfc',
    padding: '16px',
    borderRadius: '8px',
    border: `1px solid ${theme.grayBorderColor}`
  },
  introMessage: {
    fontSize: '14px',
    lineHeight: 1.6,
    color: theme.textColor,
    whiteSpace: 'pre-line' as const
  },
  instructionBox: {
    backgroundColor: '#e6f4fb',
    borderLeft: `4px solid ${theme.linkColor}`,
    borderRadius: '4px',
    padding: '14px'
  },
  instructionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '6px'
  },
  instructionIcon: {
    fontSize: '14px'
  },
  instructionTitle: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.linkColor
  },
  instructionList: {
    margin: '0 0 8px 0',
    fontSize: '12px',
    color: theme.grayTextColor,
    lineHeight: 1.6
  },
  instructionNote: {
    margin: 0,
    fontSize: '11px',
    color: theme.grayTextColor
  }
});

export default ProposalCards;
